import { useMutation } from '@tanstack/react-query';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from '@cyphex/ui';
import type { ChannelId, Uuid } from '@cyphex/types/common';
import type { CommandResult } from '@cyphex/types/platform';
import { apiPost } from '@/lib/api/client';

export interface ExecuteCommandInput {
  command: string;
  text: string;
  channelId?: ChannelId;
  conversationId?: Uuid;
}

/**
 * Run a slash command from the composer.
 *
 * WHAT HAPPENS TO THE RESULT is the interesting part:
 *   - `posted` means the server already wrote a message. Say nothing — the
 *     timeline is about to show it, and a toast on top would be noise.
 *   - an `ephemeral` reply is shown as a toast. It is private to the caller by
 *     definition, so it must NOT be injected into the conversation.
 *   - `effect` is a built-in asking the client to do something local. Only
 *     built-ins can produce one; an app-backed command cannot drive the client.
 *
 * Command results are never written into the message cache directly. The message
 * arrives over the realtime channel like any other, which keeps one path into
 * the timeline instead of two that can disagree about ordering.
 */
export function useExecuteCommand(onEffect?: (result: CommandResult) => void): {
  run: (input: ExecuteCommandInput) => void;
  isPending: boolean;
} {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: (input: ExecuteCommandInput) =>
      apiPost<CommandResult>('/commands/execute', {
        command: input.command,
        text: input.text,
        ...(input.channelId === undefined ? {} : { channelId: input.channelId }),
        ...(input.conversationId === undefined ? {} : { conversationId: input.conversationId }),
      }),
    onSuccess: (result) => {
      if (result.text !== null && result.responseType === 'ephemeral') {
        toast.success(result.text);
      }
      if (result.effect !== null) {
        // `/away` and `/dnd` changed presence server-side; the viewer's own
        // presence chip is rendered from cached data and would otherwise lie
        // until the next reload.
        if (result.effect.kind === 'presence') {
          void queryClient.invalidateQueries({ queryKey: ['viewer'] });
        }
        onEffect?.(result);
      }
    },
    onError: (error: Error) => {
      toast.error(error.message || 'That command failed.');
    },
  });

  return { run: mutation.mutate, isPending: mutation.isPending };
}

/** `"/deploy staging now"` → `{ command: 'deploy', text: 'staging now' }`. */
export function parseCommandInput(raw: string): { command: string; text: string } | null {
  const trimmed = raw.trim();
  if (!trimmed.startsWith('/')) return null;
  const body = trimmed.slice(1);
  const space = body.indexOf(' ');
  if (space === -1) return { command: body.toLowerCase(), text: '' };
  return {
    command: body.slice(0, space).toLowerCase(),
    // NOT trimmed: a command whose argument is a formatted block or a quoted
    // string must receive exactly what was typed after the command word.
    text: body.slice(space + 1),
  };
}
